// Sprint INTADMIN INTADMIN.B1 (#2110) — client-side gate for /internal/*.
//
// Implements FR-02 / FR-03 / FR-04:
//
//   FR-02  wrong role      → silent redirect to /dashboard, no error shown,
//                            no hint that the route exists
//   FR-03  unauthenticated → redirect to login carrying a return URL
//   FR-04  non-admins      → never see any part of the shell, including
//                            during the in-flight probe
//
// Shape follows the existing PlatformAdminGuard.tsx: probe an admin-only
// endpoint, treat any error as denial, render `null` while the answer is
// in flight so there is no content flash. Two deliberate differences:
//
//   1. It probes GET /api/internal/session — an endpoint whose ONLY job is
//      to answer this question — rather than borrowing a data endpoint as
//      an auth oracle.
//   2. It carries a return URL (FR-03), which PlatformAdminGuard and
//      ProtectedRoute do not. The app's convention is the `?redirect=`
//      query param, consumed by LoginPage and validated by
//      lib/redirectGuard.ts.
//
// This guard is one of two independent layers. The server gate on
// /api/internal/* (authenticate + requireFullSession + requirePlatformAdmin)
// is the authoritative one and is not weakened by anything here — bypassing
// this component yields an empty shell whose every request 403s.

import { useEffect, useState, ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { apiFetch } from '../lib/api';

type Status = 'loading' | 'allowed' | 'denied' | 'unauthenticated' | 'needs_platform_login';

// ── OPEN DECISION: where does an UNAUTHENTICATED visitor to /internal go?
//
// The two sources disagree:
//   * Issue #2110 AC3        — "redirects unauthenticated users to login
//                               with return URL"          → /login
//   * B1 implementation prompt test plan — "unauthenticated → redirect to
//                               platform login (not generic login)"
//                                                          → /platform/login
//
// Shipping '/login' pending a ruling, for two concrete reasons:
//
//   1. Existence disclosure. FR-04 requires the area to be invisible to
//      non-admins, and an anonymous visitor is definitionally a non-admin.
//      Bouncing them to a PLATFORM-ADMIN login page confirms that
//      /internal is a platform-admin surface — to someone who has
//      presented no credentials at all. The generic login leaks nothing:
//      it is where every unauthenticated deep link in the app lands.
//   2. /platform/login has no <Route> in App.tsx today and the app has no
//      catch-all, so that redirect currently renders a blank page.
//
// Flipping the decision is this one constant — the return URL, the
// encoding and the tests all follow it.
const UNAUTHENTICATED_LOGIN_PATH = '/login';

export default function InternalAdminGuard({ children }: { children: ReactNode }) {
  const token = localStorage.getItem('blueprint_token');
  const location = useLocation();
  const [status, setStatus] = useState<Status>(() => (token ? 'loading' : 'unauthenticated'));

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    apiFetch('/api/internal/session')
      .then(() => { if (!cancelled) setStatus('allowed'); })
      .catch((err: unknown) => {
        if (cancelled) return;
        const e = err as { status?: number; body?: { reason?: string } } | null;

        // A token in localStorage is NOT proof of a live session — it can be
        // expired or revoked, and nothing in this app prunes it. Classify on
        // the server's answer, not on token presence: a 401 means "your
        // credentials are not good", which is the unauthenticated case, so
        // these callers get the login redirect WITH their return URL rather
        // than a silent bounce to a /dashboard whose own calls would all 401
        // too. (ProtectedRoute has the same presence-only weakness; not
        // widening this change to touch it.)
        if (e?.status === 401) {
          setStatus('unauthenticated');
          return;
        }

        // #2110 Q8 — the API distinguishes "you are not an admin" from
        // "you are an admin but this session did not come from
        // /auth/platform/login" via a `reason` field. Collapsing both into
        // the same silent /dashboard bounce is indistinguishable from
        // "you're not an admin" and reliably generates support tickets.
        if (e?.body?.reason === 'PLATFORM_LOGIN_REQUIRED') {
          setStatus('needs_platform_login');
          return;
        }

        // Everything else — 403, a network failure, a 5xx — is treated as
        // denied. Fail closed: an error we cannot classify must never admit
        // anyone to the shell.
        setStatus('denied');
      });

    return () => { cancelled = true; };
  }, [token]);

  // Render nothing while deciding. Returning a spinner here would confirm
  // to a non-admin that something exists at this URL (FR-04).
  if (status === 'loading') return null;

  if (status === 'needs_platform_login') {
    // #2110 Q8 asks us to "route to platform login" instead of the generic
    // silent bounce. The honest problem: there IS no platform login page.
    // `POST /auth/platform/login` exists, but `/platform/login` has no
    // <Route> in App.tsx and this app has no catch-all, so BOTH a
    // <Navigate> and a plain <a href> land on a blank screen. Filed as
    // #2115; the other dead reference (PlatformMfaSetupPage.tsx:348)
    // predates this story.
    //
    // So the notice deliberately ships with NO link to that route. Telling
    // this admin exactly why they were stopped is the part of Q8 we can
    // actually deliver today; handing them a button to a blank page would
    // just be the same dead end with an extra click. When #2115 lands, add
    // the CTA back (or replace this whole branch with a <Navigate>).
    //
    // Safe to show real detail here: reaching this branch REQUIRES a
    // platform_admins row, so nothing leaks to a non-admin.
    return <PlatformLoginRequiredNotice />;
  }

  if (status === 'unauthenticated') {
    // FR-03 — no credentials (or credentials the server rejected): login,
    // carrying the return URL. Only the path+search is forwarded; LoginPage
    // runs it through isSafeRedirect before navigating, which rejects
    // anything not same-origin relative.
    return (
      <Navigate
        to={`${UNAUTHENTICATED_LOGIN_PATH}?redirect=${encodeURIComponent(returnPath(location))}`}
        replace
      />
    );
  }

  if (status === 'denied') {
    // FR-02 — authenticated but not an admin: silent bounce, no message,
    // no return URL (sending them back would loop them through this guard).
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function returnPath(location: { pathname: string; search: string }): string {
  return `${location.pathname}${location.search}`;
}

function PlatformLoginRequiredNotice() {
  return (
    <div
      data-testid="internal-platform-login-required"
      style={{
        minHeight:      '100vh',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        padding:        24,
        background:     'var(--bg)',
      }}
    >
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize:   20,
            fontWeight: 600,
            color:      'var(--text)',
            marginBottom: 12,
          }}
        >
          Platform login required
        </h1>
        <p
          style={{
            fontFamily:   'var(--font-body)',
            fontSize:     14,
            lineHeight:   1.6,
            color:        'var(--text-muted)',
            marginBottom: 24,
          }}
        >
          Your platform admin account is set to require sign-in through the
          dedicated platform surface (password + authenticator). Your current
          session was created through the standard login, so it can&rsquo;t be
          used for the internal admin area.
        </p>
        <p
          style={{
            fontFamily:   'var(--font-body)',
            fontSize:     13,
            lineHeight:   1.6,
            color:        'var(--text-muted)',
            marginBottom: 24,
          }}
        >
          That sign-in page isn&rsquo;t available in the app yet. Sign in
          against <code>POST /auth/platform/login</code> to obtain a platform
          session, or ask a SUPER_ADMIN to clear{' '}
          <code>requires_platform_login</code> on your account.
        </p>
        <div>
          <Link
            to="/dashboard"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize:   13,
              color:      'var(--text-muted)',
            }}
          >
            Back to app
          </Link>
        </div>
      </div>
    </div>
  );
}
